import template from "./report-invoices.html";
import "./report-invoices.less";

import invoiceListModule from "../invoice-list/invoice-list";

import { uniqBy } from "lodash";

import {
	EVENT_VALUE_CHANGED,
	EVENT_DETAIL_DIRTY,
	EVENT_DETAIL_TOGGLE
} from "../../constants";
import { FORM_REPORT_INVOICES } from "../../routes";
import { globs } from "../../services/globs";

export function controller(
	$logger,
	api,
	appState,
	loadingService,
	$uibModal,
	toastr,
	$timeout,
	entityManager,
	$scope,
	securityService,
	entityBusinessService
) {
	const ctrl = this;
	ctrl.invoices = [];

	ctrl.$onInit = () => {
		ctrl.eventBus = ctrl.reportScope.events;
		ctrl.shouldHideTable = true;

		ctrl.canCreateNewInvoiceGlob = globs.reports.byId("*").invoices.edit;
		ctrl.canReadInvoicesGlob = globs.reports.byId("*").invoices.read;
		ctrl.canReadBusinessesGlob = globs.business.byType("*").read;

		const canReadInvoices = securityService.isPatternAllowed(ctrl.canReadInvoicesGlob);
		const canReadBusinesses = securityService.isPatternAllowed(ctrl.canReadBusinessesGlob);
		ctrl.deductionsFeature = appState.forms.selected.Features?.find(f => f.Type === "Deductions");

		if(ctrl.deductionsFeature !== undefined && canReadInvoices && canReadBusinesses) {
			ctrl.businessEntityType = appState.forms.selected.Agency.Types.find(t => t.Businesses.Code === "SUPPORTED");
			ctrl.businessEntityLabel = ctrl.businessEntityType?.Name;

			ctrl.customerEntityType = appState.forms.selected.Agency.Types.find(t => t.Customers.Code === "SUPPORTED");
			ctrl.customerEntityLabel = ctrl.customerEntityType?.Name;

			$timeout(() => {
				const reportValues = (ctrl.getReportValues(true) ?? []).filter(v => v.IsDeleted !== true);

				if(validateReportValues(reportValues)) {
					setEntityValues(reportValues);
					loadInvoices();
				}

				ctrl.eventBus.register(
					EVENT_VALUE_CHANGED(ctrl.businessEntityFieldId.Field.Id),
					() => {
						handleValueUpdate();
					},
					$scope
				);

				ctrl.eventBus.register(
					EVENT_DETAIL_DIRTY,
					() => {
						handleValueUpdate();
					},
					$scope
				);

				ctrl.eventBus.register(
					EVENT_DETAIL_TOGGLE,
					() => {
						handleValueUpdate();
					},
					$scope
				);
			}, 0);
		}
	};

	function handleValueUpdate() {
		const lastBusinessEntityId = ctrl.businessEntity?.Id;

		const newReportValues = ctrl.getReportValues(true);
		if(validateReportValues(newReportValues)) {
			setEntityValues(newReportValues);

			if(lastBusinessEntityId !== ctrl.businessEntity?.Id) {
				loadInvoices();
			}
		}
	}

	ctrl.newInvoice = async() => {
		const reportValues = ctrl.getReportValues(true);
		if(!validateReportValues(reportValues)) {
			return;
		}

		setEntityValues(reportValues);

		try {
			const apiResponse = await $uibModal.open({
				component: "create-invoice-modal",
				size: "md",
				backdrop: "static",
				resolve: {
					businesses: () => ctrl.businesses,
					customerEntities: () => ctrl.customerEntities,
					reportValues: () => (ctrl.getReportValues(true) ?? []).filter(v => v.IsDeleted !== true),
					currentInvoices: () => ctrl.invoices,
					deductionsFeature: () => ctrl.deductionsFeature,
					save: () => saveNewInvoice
				}
			})?.result;

			const newInvoice = {
				Amount: apiResponse.resp.Amount,
				Invoice: {
					...apiResponse.resp.Invoice,
					Customer: {
						Id: apiResponse.customer.Id,
						Name: apiResponse.customer.Name
					}
				}
			};
			ctrl.invoices.push(newInvoice);

			await loadInvoices();
		}
		catch(err) {
			// do nothing if user cancels modal
		}
	};

	function validateReportValues(reportValues) {
		if(ctrl.businessEntityType === undefined || ctrl.customerEntityType === undefined) {
			ctrl.shouldHideTable = true;

			return false;
		}

		const customerEntityFieldId = ctrl.deductionsFeature.Fields.find(f => f.Properties.Type === "CUSTOMER");
		ctrl.businessEntityFieldId = ctrl.deductionsFeature.Fields.find(f => f.Properties.Type === "BUSINESS");
		const quantityFieldId = ctrl.deductionsFeature.Fields.find(f => f.Properties.Type === "QUANTITY");
		const priceFieldId = ctrl.deductionsFeature.Fields.find(f => f.Properties.Type === "PRICE");

		if(ctrl.businessEntityFieldId?.Field?.Id === undefined
			|| customerEntityFieldId?.Field?.Id === undefined
			|| quantityFieldId?.Field?.Id === undefined
			|| priceFieldId?.Field?.Id === undefined) {
			toastr.error("The deductions feature is malformed");

			ctrl.shouldHideTable = true;

			return false;
		}

		const businessEntityFieldValue = reportValues.find(v => v.FieldId === ctrl.businessEntityFieldId.Field.Id);
		const customerEntityFieldValues = reportValues.filter(v => v.FieldId === customerEntityFieldId.Field.Id);
		const hasCustomerValues = customerEntityFieldValues.some(v => v.EntityId !== undefined);

		if(businessEntityFieldValue?.EntityId === undefined || !hasCustomerValues) {
			ctrl.shouldHideTable = true;

			return false;
		}

		return true;
	}

	function setEntityValues(reportValues) {
		const customerEntityFieldId = ctrl.deductionsFeature.Fields.find(f => f.Properties.Type === "CUSTOMER");
		const businessEntityFieldValue = reportValues.find(v => v.FieldId === ctrl.businessEntityFieldId.Field.Id);
		const customerEntityFieldValues = reportValues.filter(v => v.FieldId === customerEntityFieldId.Field.Id);

		const businessEntityField = appState.forms.selected.Fields.find(f => f.EntityQueryConfiguration?.Friendly?.Type.Code === ctrl.businessEntityType.Code);
		const customerEntityField = appState.forms.selected.Fields.find(f => f.EntityQueryConfiguration?.Friendly?.Type.Code === ctrl.customerEntityType.Code);

		const friendlyBusinessEntity = entityManager.findFriendlyByPrimaryId(businessEntityField.Data, businessEntityFieldValue.EntityId);
		const customerBusinessEntities = customerEntityFieldValues
			.filter(v => v.EntityId !== undefined)
			.filter(v => v.IsDeleted !== true)
			.map(v => entityManager.findFriendlyByPrimaryId(customerEntityField.Data, v.EntityId));

		ctrl.businessEntity = friendlyBusinessEntity;
		ctrl.customerEntities = uniqBy(customerBusinessEntities, e => e.Id);
	}

	function groupInvoicesByCustomer(invoices = []) {
		return Object.values(
			(invoices ?? []).reduce((groups, customerInvoice) => {
				const customer = customerInvoice?.Invoice?.Customer ?? {};
				const customerKey = customer.Id ?? customer.Name ?? "unknown";
				const name = customer.Name ?? "Unknown";

				if(!groups[customerKey]) {
					groups[customerKey] = {
						customerName: name,
						items: [],
						totals: { Subtotal: 0, TaxAndFees: 0, Total: 0, DueCredit: 0, DueDeductions: 0 }
					};
				}

				groups[customerKey].items.push(customerInvoice);
				const t = groups[customerKey].totals;
				t.Subtotal += Number(customerInvoice?.Invoice?.Subtotal ?? 0);
				t.TaxAndFees += Number(customerInvoice?.Invoice?.TaxAndFees ?? 0);
				t.Total += Number(customerInvoice?.Invoice?.Total ?? 0);
				t.DueCredit += Number(customerInvoice?.Due?.Credit ?? 0);
				t.DueDeductions += Number(customerInvoice?.Due?.Deductions ?? 0);
				return groups;
			}, {})
		);
	}
	async function loadInvoices() {
		if(!ctrl.reportMetadata?.reverseSortedHistory.length) {
			ctrl.invoices = [];

			return;
		}

		try {
			const loadingBusiness = entityBusinessService.getBusinessesForEntity(appState.forms.selected.Id, ctrl.businessEntity.Id);
			ctrl.businesses = await loadingService.watch(loadingBusiness);

			if(ctrl.businesses.length === 0) {
				ctrl.invoices = [];
				ctrl.noBusinessConfigured = true;
				ctrl.shouldHideTable = true;

				return;
			}

			ctrl.noBusinessConfigured = false;
			ctrl.shouldHideTable = false;

			const invoiceRequests = ctrl.businesses.map(b =>
				api.get(FORM_REPORT_INVOICES(appState.forms.selected.Id, ctrl.reportMetadata.report.Id, b.Business.Id))
			);

			const allInvoicesByBusiness = await loadingService.watch(Promise.all(invoiceRequests));

			ctrl.invoices = allInvoicesByBusiness.flatMap((list, idx) =>
				(list ?? []).map(i => ({
					...i,
					Business: ctrl.businesses[idx].Business
				}))
			);

			ctrl.invoices.forEach(i => {
				i.Due = {
					Credit: i.Amount,
					Deductions: i.Amount - i.Invoice.Subtotal
				};
			});

			ctrl.hasVoidedInvoices = ctrl.invoices.some(i => i.Invoice.State?.IsVoid === true);

			ctrl.groupedInvoices = groupInvoicesByCustomer(ctrl.invoices);

		}
		catch(err) {
			$logger.exception(err, "txn load error");
			toastr.error("There was an error loading invoices.");
		}
	}

	async function saveNewInvoice(businessId, customerId, invoice) {
		try {
			const postingInvoice =
				api.post(FORM_REPORT_INVOICES(appState.forms.selected.Id, ctrl.reportMetadata.report.Id, businessId, customerId), invoice);
			const resp = await loadingService.watch(postingInvoice);

			toastr.success("Invoice created successfully!");
			return resp;
		}
		catch(err) {
			$logger.exception(err, "create invoice error");
			toastr.error("There was an error creating the invoice.");
		}
	}
}

export default angular.module("vesl.components.report-invoices", [invoiceListModule]).component("reportInvoices", {
	template,
	controller,
	bindings: {
		form: "<",
		getReportValues: "=",
		reportMetadata: "<"
	},
	require: {
		reportScope: "^^reportScope"
	}
}).name;
